import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, Plus } from "lucide-react";
import { useState } from "react";
import { CreateAuditDialog } from "@/components/audits/CreateAuditDialog";
import { ProjectAudits } from "@/components/projects/ProjectAudits";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useProject } from "@/hooks/use-projects";
import { useSession } from "@/lib/auth-client";

export function ProjectDetailPage() {
  const { projectId } = useParams({ from: "/dashboard/projects/$projectId" });
  const navigate = useNavigate();
  const { data: project, isLoading, isError, refetch } = useProject(projectId);
  const { data: session } = useSession();
  const [createAuditOpen, setCreateAuditOpen] = useState(false);

  const role = session?.user?.role ?? "";
  const canCreateAudit = role === "admin" || role === "compliance_manager";

  if (isError) {
    return <ErrorState message="Failed to load project" onRetry={() => refetch()} />;
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-start gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate({ to: "/dashboard/projects" })}
            className="mt-1 shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>

          <div className="min-w-0 flex-1">
            {isLoading ? (
              <div className="space-y-2">
                <div className="h-8 w-48 bg-muted rounded animate-pulse" />
                <div className="h-4 w-96 bg-muted rounded animate-pulse" />
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
                    {project?.name}
                  </h1>
                  {project && (
                    <Badge
                      variant="outline"
                      className={
                        project.isActive
                          ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-transparent"
                          : "bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-transparent"
                      }
                    >
                      {project.isActive ? "Active" : "Inactive"}
                    </Badge>
                  )}
                </div>
                {project?.accountName && (
                  <p className="text-sm text-muted-foreground pt-0.5">{project.accountName}</p>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Audits</h2>
            {canCreateAudit && (
              <Button size="sm" onClick={() => setCreateAuditOpen(true)}>
                <Plus className="h-4 w-4 mr-1" />
                New Audit
              </Button>
            )}
          </div>
          <ProjectAudits projectId={projectId} />
        </div>
      </div>

      <CreateAuditDialog
        open={createAuditOpen}
        onOpenChange={setCreateAuditOpen}
        defaultProjectId={projectId}
      />
    </>
  );
}
